import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { auth, db, isFirebaseConfigured, requestFcmToken, listenForegroundMessages } from '../firebase';
import i18n, { SUPPORTED_LOCALES } from '../i18n';
import { clearLocalUserData } from '../services/LocalDataService';
import { XpService } from '../services/XpService';
import { StreakService } from '../services/StreakService';
import { SrsService } from '../services/SrsService';
import { AchievementsService } from '../services/AchievementsService';
import { ReferralService } from '../services/ReferralService';

const AuthContext = createContext(null);
const GUEST_PREFS_KEY = 'sm_lang_guest_prefs';
const DEFAULT_LANGUAGE_PREFS = Object.freeze({
  nativeLanguage: 'en',
  targetLanguage: 'es',
});

// Display language mirrors the user's native language whenever it has a UI
// translation — there is no separate manual display-language selector.
function getUserPrefsKey(userId) {
  return `sm_lang_user_prefs_${userId}`;
}

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

function hasLanguagePair(prefs) {
  return Boolean(prefs?.nativeLanguage && prefs?.targetLanguage);
}

function normalizeLanguagePrefs(prefs) {
  return hasLanguagePair(prefs) ? prefs : { ...DEFAULT_LANGUAGE_PREFS };
}

// Caps the delete loop well above any realistic SRS collection size
// (400 docs/batch × 500 = 200,000 cards) so a permissions edge case that
// keeps returning docs without actually deleting them can't hang the
// "Deleting…" button forever.
const MAX_SRS_DELETE_BATCHES = 500;

async function deleteFirestoreUserData(uid) {
  if (!db) return;
  const srs = collection(db, 'users', uid, 'srs');
  for (let i = 0; i < MAX_SRS_DELETE_BATCHES; i++) {
    const snapshot = await getDocs(query(srs, limit(400)));
    if (snapshot.empty) break;
    const batch = writeBatch(db);
    snapshot.docs.forEach((entry) => batch.delete(entry.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, 'users', uid));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [languagePrefs, setLanguagePrefsState] = useState({ ...DEFAULT_LANGUAGE_PREFS });
  const [isLanguageSelectionOpen, setIsLanguageSelectionOpen] = useState(false);

  // Sync the display language to native when available; otherwise use English.
  const applyLanguagePrefs = (prefs) => {
    setLanguagePrefsState(prefs);
    if (prefs?.nativeLanguage) {
      i18n.changeLanguage(
        SUPPORTED_LOCALES.includes(prefs.nativeLanguage)
          ? prefs.nativeLanguage
          : 'en',
      );
    }
  };

  // Load prefs: Firestore (logged-in) → localStorage fallback
  useEffect(() => {
    if (!isReady) return;
    let active = true;
    if (!user) {
      const storedPrefs = readJson(GUEST_PREFS_KEY, null);
      const prefs = normalizeLanguagePrefs(storedPrefs);
      applyLanguagePrefs(prefs);
      return () => { active = false; };
    }
    const localKey = getUserPrefsKey(user.id);
    if (db) {
      getDoc(doc(db, 'users', user.id))
        .then((snap) => {
          if (!active) return;
          if (snap.exists()) {
            const remoteData = snap.data();
            XpService.hydrateFromRemote(remoteData);
            StreakService.hydrateFromRemote(remoteData);
            AchievementsService.hydrateFromRemote(remoteData);
          }
          SrsService.hydrateFromRemote(user.id);
          if (snap.exists() && hasLanguagePair(snap.data().languagePrefs)) {
            const prefs = normalizeLanguagePrefs(snap.data().languagePrefs);
            writeJson(localKey, prefs);           // cache locally
            applyLanguagePrefs(prefs);
          } else {
            const cachedPrefs = readJson(localKey, null);
            const prefs = normalizeLanguagePrefs(cachedPrefs);
            writeJson(localKey, prefs);
            applyLanguagePrefs(prefs);
            setDoc(
              doc(db, 'users', user.id),
              { languagePrefs: prefs },
              { merge: true },
            ).catch(() => {});
          }
        })
        .catch(() => {
          if (!active) return;
          const prefs = normalizeLanguagePrefs(readJson(localKey, null));
          writeJson(localKey, prefs);
          applyLanguagePrefs(prefs);
        });
    } else {
      const prefs = normalizeLanguagePrefs(readJson(localKey, null));
      writeJson(localKey, prefs);
      applyLanguagePrefs(prefs);
    }
    return () => { active = false; };
  }, [user, isReady]);

  const setLanguagePreferences = (prefs) => {
    const localKey = user ? getUserPrefsKey(user.id) : GUEST_PREFS_KEY;
    writeJson(localKey, prefs);
    applyLanguagePrefs(prefs);
    setIsLanguageSelectionOpen(false);
    if (user && db) {
      setDoc(doc(db, 'users', user.id), { languagePrefs: prefs }, { merge: true }).catch(() => {});
    }
  };

  const openLanguagePreferences = () => setIsLanguageSelectionOpen(true);
  const closeLanguagePreferences = () => setIsLanguageSelectionOpen(false);

  useEffect(() => {
    let active = true;
    if (!auth || !isFirebaseConfigured) {
      setUser(null);
      setIsReady(true);
      return () => { active = false; };
    }

    let unsubForeground = () => {};
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!active) return;
      if (firebaseUser) {
        const usesPasswordProvider = firebaseUser.providerData.some(
          (provider) => provider.providerId === 'password',
        );
        if (usesPasswordProvider && !firebaseUser.emailVerified) {
          await signOut(auth);
          if (!active) return;
          setUser(null);
        } else {
          setUser({
            id: firebaseUser.uid,
            name: firebaseUser.displayName || '',
            email: firebaseUser.email || '',
            phone: firebaseUser.phoneNumber || '',
            isAnonymous: firebaseUser.isAnonymous,
          });
          // Save FCM token and start foreground message listener
          requestFcmToken().then((token) => {
            if (token && db) {
              setDoc(doc(db, 'users', firebaseUser.uid), { fcmToken: token }, { merge: true }).catch(() => {});
            }
          });
          unsubForeground();
          unsubForeground = listenForegroundMessages();
        }
      } else {
        setUser(null);
        unsubForeground();
      }
      if (!active) return;
      setIsReady(true);
    });
    return () => { active = false; unsub(); unsubForeground(); };
  }, []);

  const login = async (email, password) => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Sign in is temporarily unavailable. Please try again later.' };
    }

    try {
      await signInWithEmailAndPassword(auth, String(email || '').trim(), String(password || ''));
      if (!auth.currentUser?.emailVerified) {
        await signOut(auth);
        return { ok: false, message: 'Please verify your email before signing in. Check your inbox for a verification link.' };
      }
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/user-not-found' ||
        code === 'auth/wrong-password'
      ) {
        return { ok: false, message: 'Invalid email or password.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      return { ok: false, message: 'Unable to login right now. Please try again.' };
    }
  };

  const register = async ({ name, email, password, referralCode }) => {
    const cleanName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    const cleanPassword = String(password || '');

    if (!cleanName || !normalizedEmail || !cleanPassword) {
      return { ok: false, message: 'All fields are required.' };
    }
    if (cleanPassword.length < 6) {
      return { ok: false, message: 'Password must be at least 6 characters.' };
    }

    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Registration is temporarily unavailable. Please try again later.' };
    }

    try {
      const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, cleanPassword);
      await updateProfile(credential.user, { displayName: cleanName });
      // Must apply the referral code before signOut() below — it writes
      // `referredBy` onto the new user's own doc, which requires this
      // session's auth.currentUser to still be the new account.
      if (referralCode) {
        await ReferralService.applyCode(referralCode, credential.user.uid).catch(() => {});
      }
      await sendEmailVerification(credential.user);
      await signOut(auth);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/email-already-in-use') {
        return { ok: false, message: 'An account with this email already exists.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      if (code === 'auth/weak-password') {
        return { ok: false, message: 'Password is too weak. Use at least 6 characters.' };
      }
      return { ok: false, message: 'Unable to register right now. Please try again.' };
    }
  };

  // Upgrades the current anonymous guest session in place (via credential
  // linking) instead of creating a separate account — preserves the uid so
  // Firestore-synced progress (XP/streak/SRS) isn't orphaned. Email/password
  // only (no Google/Apple linking yet). See AuthService.linkAnonymousWithEmail
  // in the mobile repo for the equivalent.
  const linkAnonymousWithEmail = async ({ name, email, password, referralCode }) => {
    const cleanName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    const cleanPassword = String(password || '');

    if (!cleanName || !normalizedEmail || !cleanPassword) {
      return { ok: false, message: 'All fields are required.' };
    }
    if (cleanPassword.length < 6) {
      return { ok: false, message: 'Password must be at least 6 characters.' };
    }

    const firebaseUser = auth?.currentUser;
    if (!auth || !isFirebaseConfigured || !firebaseUser?.isAnonymous) {
      return { ok: false, message: 'Guest upgrade is unavailable right now. Please try again.' };
    }

    try {
      const credential = EmailAuthProvider.credential(normalizedEmail, cleanPassword);
      const { user: linkedUser } = await linkWithCredential(firebaseUser, credential);
      await updateProfile(linkedUser, { displayName: cleanName });
      // Must apply the referral code before signOut() below — it writes
      // `referredBy` onto the account's own doc, which requires this
      // session's auth.currentUser to still be the linked account.
      if (referralCode) {
        await ReferralService.applyCode(referralCode, linkedUser.uid).catch(() => {});
      }
      await sendEmailVerification(linkedUser);
      await signOut(auth);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/email-already-in-use' || code === 'auth/credential-already-in-use') {
        return { ok: false, message: 'An account with this email already exists.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      if (code === 'auth/weak-password') {
        return { ok: false, message: 'Password is too weak. Use at least 6 characters.' };
      }
      return { ok: false, message: 'Unable to create account right now. Please try again.' };
    }
  };

  const sendPasswordReset = async (email) => {
    const normalizedEmail = String(email || '').trim();
    if (!normalizedEmail) {
      return { ok: false, message: 'Please enter your email address.' };
    }
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Password reset requires Firebase configuration.' };
    }
    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      // Do not reveal whether the account exists.
      if (code === 'auth/user-not-found' || code === 'auth/missing-android-pkg-name') {
        return { ok: true };
      }
      return { ok: false, message: 'Unable to send reset email right now. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      if (auth && isFirebaseConfigured) await signOut(auth);
    } catch (error) {
      // Sign-out failing (e.g. offline) shouldn't block clearing local
      // session state below or leave callers with an unhandled rejection —
      // the user still ends up looking/being logged out locally.
      console.warn('AuthContext: signOut failed', error);
    } finally {
      clearLocalUserData();
      setUser(null);
      setLanguagePrefsState({ ...DEFAULT_LANGUAGE_PREFS });
      await i18n.changeLanguage('en');
    }
  };

  const deleteAccount = async ({ password = '' } = {}) => {
    const firebaseUser = auth?.currentUser;
    if (!firebaseUser || !isFirebaseConfigured) {
      return { ok: false, message: 'You must be signed in to delete your account.' };
    }

    try {
      const providers = new Set(firebaseUser.providerData.map((entry) => entry.providerId));
      if (providers.has('apple.com')) {
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        await reauthenticateWithPopup(firebaseUser, provider);
      } else if (providers.has('google.com')) {
        await reauthenticateWithPopup(firebaseUser, new GoogleAuthProvider());
      } else if (providers.has('password')) {
        if (!firebaseUser.email || !password) {
          return { ok: false, message: 'Enter your password to confirm account deletion.' };
        }
        await reauthenticateWithCredential(
          firebaseUser,
          EmailAuthProvider.credential(firebaseUser.email, password),
        );
      }

      await deleteFirestoreUserData(firebaseUser.uid);
      await deleteUser(firebaseUser);
      clearLocalUserData();
      setUser(null);
      setLanguagePrefsState({ ...DEFAULT_LANGUAGE_PREFS });
      await i18n.changeLanguage('en');
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return { ok: false, message: 'Account deletion was cancelled.' };
      }
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        return { ok: false, message: 'The password is incorrect. Your account was not deleted.' };
      }
      if (code === 'auth/requires-recent-login') {
        return { ok: false, message: 'Sign out, sign in again, and retry account deletion.' };
      }
      return { ok: false, message: 'Your account could not be deleted. Please try again.' };
    }
  };

  // Firebase Anonymous Auth — gives a guest a real uid (so progress/XP/streak
  // sync to Firestore like any other account) without requiring an email or
  // password up front. See AuthService.signInAnonymously in the mobile repo
  // for the equivalent.
  const continueAsGuest = async () => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Guest sign-in requires Firebase configuration.' };
    }
    try {
      await signInAnonymously(auth);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      console.error('[Guest login error]', code, error?.message);
      return { ok: false, message: 'Unable to continue as guest right now. Please try again.' };
    }
  };

  const loginWithGoogle = async () => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Google login requires Firebase configuration.' };
    }
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      console.error('[Google login error]', code, error?.message);
      if (code === 'auth/popup-closed-by-user') {
        return { ok: false, message: 'Google login was cancelled.' };
      }
      return { ok: false, message: `Google login failed: ${code || error?.message || 'unknown error'}` };
    }
  };

  const loginWithApple = async () => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Apple login requires Firebase configuration.' };
    }
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      provider.setCustomParameters({ locale: i18n.resolvedLanguage || 'en' });
      await signInWithPopup(auth, provider);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      console.error('[Apple login error]', code, error?.message);
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request'
      ) {
        return { ok: false, message: 'Apple login was cancelled.' };
      }
      if (code === 'auth/account-exists-with-different-credential') {
        return {
          ok: false,
          message: 'An account already exists with this email. Sign in using the original method, then link Apple from your account.',
        };
      }
      if (code === 'auth/operation-not-allowed') {
        return { ok: false, message: 'Apple login is not enabled for this Firebase project.' };
      }
      if (code === 'auth/unauthorized-domain') {
        return { ok: false, message: 'This domain is not authorized for Apple login.' };
      }
      return { ok: false, message: 'Apple login failed. Please try again.' };
    }
  };

  const sendPhoneOTP = async (phoneNumber, appVerifier) => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Phone login requires Firebase configuration.' };
    }
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      return { ok: true, confirmationResult };
    } catch (error) {
      const code = error?.code || '';
      console.error('[Phone OTP error]', code, error?.message);
      if (code === 'auth/invalid-phone-number') {
        return { ok: false, message: 'Invalid phone number. Use international format (+1234567890).' };
      }
      if (code === 'auth/too-many-requests') {
        return { ok: false, message: 'Too many attempts. Please try again later.' };
      }
      if (code === 'auth/operation-not-allowed') {
        return { ok: false, message: 'Phone authentication is not enabled for this project.' };
      }
      if (code === 'auth/quota-exceeded') {
        return { ok: false, message: 'SMS quota exceeded. Please try again later.' };
      }
      if (code === 'auth/missing-phone-number') {
        return { ok: false, message: 'Please enter a phone number.' };
      }
      if (code === 'auth/billing-not-enabled') {
        return { ok: false, message: 'Phone login requires a Firebase Blaze plan. Please upgrade your Firebase project billing.' };
      }
      return { ok: false, message: `Unable to send OTP (${code || 'unknown'}). Please try again.` };
    }
  };

  const verifyPhoneOTP = async (confirmationResult, code) => {
    try {
      await confirmationResult.confirm(code);
      return { ok: true };
    } catch (error) {
      const errCode = error?.code || '';
      if (errCode === 'auth/invalid-verification-code') {
        return { ok: false, message: 'Invalid code. Please check and try again.' };
      }
      if (errCode === 'auth/code-expired') {
        return { ok: false, message: 'Code expired. Please request a new one.' };
      }
      return { ok: false, message: 'Unable to verify code. Please try again.' };
    }
  };

  const providerIds = new Set(
    auth?.currentUser?.providerData.map((entry) => entry.providerId) ?? [],
  );
  const usesPasswordProvider = providerIds.has('password')
    && !providerIds.has('google.com')
    && !providerIds.has('apple.com');

  const value = {
    user,
    isReady,
    isAuthenticated: Boolean(user),
    isAnonymous: Boolean(user?.isAnonymous),
    languagePrefs,
    isLanguageSelectionOpen,
    setLanguagePreferences,
    openLanguagePreferences,
    closeLanguagePreferences,
    login,
    register,
    linkAnonymousWithEmail,
    logout,
    deleteAccount,
    usesPasswordProvider,
    continueAsGuest,
    loginWithGoogle,
    loginWithApple,
    sendPasswordReset,
    sendPhoneOTP,
    verifyPhoneOTP,
    isFirebaseConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
